import React, { useLayoutEffect, useRef, useState } from 'react'
import { TutorialStep, TutorialView } from '../lib/tutorial'
import { SidebarTab } from '../types'

interface Props {
  steps: TutorialStep[]
  currentView: TutorialView
  onSetView: (view: TutorialView) => void
  currentTab: SidebarTab
  onSetTab: (tab: SidebarTab) => void
  /** Whether the sidebar drawer is currently open. */
  sidebarOpen: boolean
  /** Opens or closes it — on a phone it's where most of these steps point. */
  onSetSidebarOpen: (open: boolean) => void
  onFinish: () => void
}

const MARGIN = 12

/** How long the target's box has to hold still before we stop re-measuring it. */
const SETTLE_MS = 250

/**
 * How long to keep looking for a target that isn't in the DOM yet. The sandbox
 * step needs this: its panel arrives with a lazily-loaded chunk, so on a slow
 * connection it can be a second or more behind the step opening.
 */
const MAX_WAIT_MS = 4000

export default function Tutorial({
  steps,
  currentView,
  onSetView,
  currentTab,
  onSetTab,
  sidebarOpen,
  onSetSidebarOpen,
  onFinish,
}: Props) {
  const [index, setIndex] = useState(0)
  const [rect, setRect] = useState<DOMRect | null>(null)
  const [cardPos, setCardPos] = useState<{ top: number; left: number }>({ top: MARGIN, left: MARGIN })
  const cardRef = useRef<HTMLDivElement>(null)

  const step = steps[index]
  // The view, the sidebar tab and — on a phone — the drawer all gate what is on
  // screen, so a step isn't measurable until every one of them has caught up.
  // The drawer matters most: without it in this list, the effect below would
  // measure the sidebar while it was still parked off the left edge and put the
  // highlight where nobody can see it.
  const ready =
    (!step.view || step.view === currentView) &&
    (!step.tab || step.tab === currentTab) &&
    (!step.sidebar || sidebarOpen)

  // Ask the app to switch to the view / tab this step needs.
  useLayoutEffect(() => {
    if (step.view && step.view !== currentView) onSetView(step.view)
    if (step.tab && step.tab !== currentTab) onSetTab(step.tab)
    onSetSidebarOpen(!!step.sidebar)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index])

  // Measure the target once everything it depends on is actually showing.
  useLayoutEffect(() => {
    if (!ready) {
      setRect(null)
      return
    }

    function find(): HTMLElement | null {
      // Some targets exist twice — a control laid out one way for a phone and
      // another for a desktop, with the wrong one display:none. Taking the
      // first match would highlight an empty rectangle at the top left.
      const candidates = document.querySelectorAll<HTMLElement>(`[data-tour="${step.target}"]`)
      return Array.from(candidates).find((c) => c.getClientRects().length > 0) ?? null
    }

    // getBoundingClientRect hands back a new object every time, so this
    // compares the numbers instead — otherwise the loop below would re-render
    // on every frame whether or not anything had moved.
    let lastKey = ''
    /** @returns true when the target's position changed since the last read. */
    function measure(): boolean {
      const el = find()
      const next = el ? el.getBoundingClientRect() : null
      const key = next ? `${next.x}:${next.y}:${next.width}:${next.height}` : 'none'
      if (key === lastKey) return false
      lastKey = key
      setRect(next)
      return true
    }

    find()?.scrollIntoView({ block: 'nearest', inline: 'nearest' })

    // One measurement taken the instant a step opens is usually of the wrong
    // thing: the drawer is still sliding, the scroll above hasn't settled, or
    // — on the sandbox step — the panel's chunk is still downloading. So poll
    // until the target has been found and has held still, rather than trusting
    // any single frame. The ring has its own CSS transition, so this cadence
    // doesn't have to be per-frame to look smooth.
    let steadySince = performance.now()
    const deadline = performance.now() + MAX_WAIT_MS
    const poll = setInterval(() => {
      const now = performance.now()
      if (measure() || lastKey === 'none') steadySince = now
      const settled = lastKey !== 'none' && now - steadySince > SETTLE_MS
      if (settled || now > deadline) clearInterval(poll)
    }, 100)
    measure()

    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    return () => {
      clearInterval(poll)
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [index, ready, step.target])

  // Position the tooltip card next to the target once both are known.
  useLayoutEffect(() => {
    if (!rect || !cardRef.current) return
    const cw = cardRef.current.offsetWidth
    const ch = cardRef.current.offsetHeight
    const vw = window.innerWidth
    const vh = window.innerHeight

    let left = rect.right + MARGIN
    let top = rect.top

    if (left + cw + MARGIN > vw) {
      left = rect.left
      top = rect.bottom + MARGIN
      if (top + ch + MARGIN > vh) {
        top = rect.top - ch - MARGIN
      }
    }
    left = Math.min(Math.max(left, MARGIN), Math.max(MARGIN, vw - cw - MARGIN))
    top = Math.min(Math.max(top, MARGIN), Math.max(MARGIN, vh - ch - MARGIN))
    setCardPos({ top, left })
  }, [rect])

  function next() {
    if (index < steps.length - 1) setIndex((i) => i + 1)
    else onFinish()
  }

  function back() {
    setIndex((i) => Math.max(0, i - 1))
  }

  return (
    <div className="fixed inset-0 z-[70]">
      {/* Blocks interaction with the app underneath while the tour is active */}
      <div className="absolute inset-0" onClick={(e) => e.stopPropagation()} />

      {rect && (
        <div
          className="fixed rounded-md ring-2 ring-accent transition-[top,left,width,height] duration-200 pointer-events-none"
          style={{
            top: rect.top - 4,
            left: rect.left - 4,
            width: rect.width + 8,
            height: rect.height + 8,
            boxShadow: '0 0 0 9999px rgba(15,23,20,0.72)',
          }}
        />
      )}

      <div
        ref={cardRef}
        // Fixed at 300px where there's room, and never wider than the screen
        // less its margins — on a 320px phone the card would otherwise hang off
        // the edge with the Next button on it.
        className="fixed bg-white border border-line rounded shadow-lg p-4 w-[min(300px,calc(100vw-24px))] z-[71]"
        style={{ top: cardPos.top, left: cardPos.left }}
      >
        <div className="text-[11px] font-sans uppercase tracking-wide text-ink/70 mb-1">
          Step {index + 1} of {steps.length}
        </div>
        <h3 className="font-display text-lg text-ink mb-2">{step.title}</h3>
        <p className="text-sm text-ink/80 mb-4">{step.body}</p>
        <div className="flex items-center justify-between">
          <button onClick={onFinish} className="text-xs text-ink/70 hover:text-ink hover:underline">
            Skip tutorial
          </button>
          <div className="flex items-center gap-2">
            {index > 0 && (
              <button
                onClick={back}
                className="px-3 py-1.5 text-sm rounded-sm border border-line text-ink/80 hover:bg-paper"
              >
                Back
              </button>
            )}
            <button onClick={next} className="px-3 py-1.5 text-sm rounded-sm bg-accent text-white hover:bg-accent/90">
              {index < steps.length - 1 ? 'Next' : 'Done'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
